import { useSessionStorage } from '@vueuse/core';
import { STORAGE_KEYS } from '~/constants/auth';
import { isSafeAuthRedirectPath, safeAuthRedirectPath } from '~/utils/auth-redirect';

/**
 * sessionStorage seam for round-tripping post-auth navigation across the IdP
 * redirect. Login/link return-tos are single-use strings; signup carries a
 * context object so the callback can reconstruct the invite URL on error.
 *
 * Values are validated as same-origin relative paths to prevent open-redirect
 * via `?redirect=https://evil.com`.
 */

export interface SsoSignupContext {
	inviteToken: string;
}

export function useSsoReturnTo() {
	const loginReturnTo = useSessionStorage<string | null>(STORAGE_KEYS.ssoLoginReturnTo, null);
	const linkReturnTo = useSessionStorage<string | null>(STORAGE_KEYS.ssoLinkReturnTo, null);
	const signupContext = useSessionStorage<SsoSignupContext | null>(STORAGE_KEYS.ssoSignupContext, null);

	function setOrClear(target: ReturnType<typeof useSessionStorage<string | null>>, value: string | undefined) {
		target.value = value && isSafeAuthRedirectPath(value) ? value : null;
	}

	function consume(target: ReturnType<typeof useSessionStorage<string | null>>): string {
		const value = target.value;
		target.value = null;
		return safeAuthRedirectPath(value);
	}

	function clear() {
		loginReturnTo.value = null;
		linkReturnTo.value = null;
		signupContext.value = null;
	}

	return {
		setLoginReturnTo: (value: string | undefined) => setOrClear(loginReturnTo, value),
		consumeLoginReturnTo: () => consume(loginReturnTo),
		setLinkReturnTo: (value: string | undefined) => setOrClear(linkReturnTo, value),
		consumeLinkReturnTo: () => consume(linkReturnTo),
		setSignupContext: (ctx: SsoSignupContext | undefined) => {
			signupContext.value = ctx ? { inviteToken: ctx.inviteToken } : null;
		},
		// Non-consuming read for the callback error branch — needs to render the
		// back button without clearing state until success/explicit dismissal.
		peekSignupContext: (): SsoSignupContext | null => signupContext.value,
		consumeSignupContext: (): SsoSignupContext | null => {
			const ctx = signupContext.value;
			signupContext.value = null;
			return ctx;
		},
		clear,
	};
}
